import {
  saveToken,
  superadminTokenKey,
  tenantTokenKey
} from '../app/session';

const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const tenantStorageKey = 'sihterica.tenantSlug';

export function apiAssetUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^(?:https?:|data:|blob:)/i.test(url)) return url;
  return url.startsWith('/api/') ? `${apiBase}${url}` : url;
}

function developmentTenantSlug(): string | null {
  if (!import.meta.env.DEV) return null;
  const fromUrl = new URLSearchParams(window.location.search).get('tenant');
  if (fromUrl && /^[a-z0-9-]{1,60}$/.test(fromUrl)) {
    sessionStorage.setItem(tenantStorageKey, fromUrl);
    return fromUrl;
  }
  return (
    import.meta.env.VITE_TENANT_SLUG ??
    sessionStorage.getItem(tenantStorageKey)
  );
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
  }
}

interface RequestOptions extends RequestInit {
  accessToken?: string | null;
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
  allowRefresh = true
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  const tenantSlug = developmentTenantSlug();
  if (tenantSlug) headers.set('x-tenant-slug', tenantSlug);
  if (options.body) headers.set('Content-Type', 'application/json');
  if (options.accessToken) {
    headers.set('Authorization', `Bearer ${options.accessToken}`);
  }

  const response = await fetch(`${apiBase}${path}`, {
    ...options,
    headers,
    credentials: 'include'
  });
  const payload = (await response.json().catch(() => null)) as
    | Record<string, unknown>
    | null;

  if (
    response.status === 401 &&
    allowRefresh &&
    options.accessToken &&
    !path.includes('/auth/')
  ) {
    const isSuperadmin = path.startsWith('/api/v1/superadmin/');
    const refreshPath = isSuperadmin
      ? '/api/v1/superadmin/auth/refresh'
      : '/api/v1/auth/refresh';
    const refreshResponse = await fetch(`${apiBase}${refreshPath}`, {
      method: 'POST',
      credentials: 'include',
      headers: tenantSlug ? { 'x-tenant-slug': tenantSlug } : undefined
    });
    if (refreshResponse.ok) {
      const refreshPayload = (await refreshResponse.json()) as {
        accessToken: string;
      };
      saveToken(
        isSuperadmin ? superadminTokenKey : tenantTokenKey,
        refreshPayload.accessToken
      );
      return apiRequest<T>(
        path,
        { ...options, accessToken: refreshPayload.accessToken },
        false
      );
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      String(payload?.error ?? 'REQUEST_FAILED'),
      String(payload?.message ?? 'Zahtjev nije uspio.')
    );
  }
  return payload as T;
}

export async function apiDownload(
  path: string,
  accessToken: string | null
): Promise<{ blob: Blob; filename: string }> {
  const headers = new Headers();
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  const tenantSlug = developmentTenantSlug();
  if (tenantSlug) headers.set('x-tenant-slug', tenantSlug);
  const response = await fetch(`${apiBase}${path}`, {
    headers,
    credentials: 'include'
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as
      | Record<string, unknown>
      | null;
    throw new ApiError(
      response.status,
      String(payload?.error ?? 'DOWNLOAD_FAILED'),
      String(payload?.message ?? 'Preuzimanje nije uspjelo.')
    );
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'dokument';
  return { blob: await response.blob(), filename };
}
